import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Menu, 
  X, 
  Calendar, 
  Users, 
  Activity, 
  CheckCircle2, 
  ArrowRight,
  Smartphone,
  ChevronRight
} from 'lucide-react';

export default function Landing() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Functie voor vloeiend scrollen met correctie voor de header
  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, targetId: string) => {
    e.preventDefault();
    setIsMobileMenuOpen(false); // Sluit mobiel menu automatisch na klikken
    const element = document.getElementById(targetId);
    if (element) {
      const headerOffset = 80; // Compenseert voor de h-20 (80px) sticky header
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 selection:bg-orange-100 selection:text-orange-900">
      
      {/* 🌐 Navigatiebalk (Header) */}
      <nav className="sticky top-0 z-50 w-full bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            
            {/* Logo */}
            <div className="flex-shrink-0 flex items-center cursor-pointer">
              <img src="/logo.png" alt="Bespanapp.nl Logo" className="h-10 sm:h-12 w-auto object-contain" />
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" onClick={(e) => handleNavClick(e, 'features')} className="text-slate-600 hover:text-[#1e3a8a] font-medium transition-colors">Functionaliteiten</a>
              <a href="#werkwijze" onClick={(e) => handleNavClick(e, 'werkwijze')} className="text-slate-600 hover:text-[#1e3a8a] font-medium transition-colors">Werkwijze</a>
              <a href="#tarieven" onClick={(e) => handleNavClick(e, 'tarieven')} className="text-slate-600 hover:text-[#1e3a8a] font-medium transition-colors">Tarieven</a>
              <div className="flex items-center gap-4 pl-4 border-l border-slate-200">
                <Link to="/dashboard" className="text-[#1e3a8a] font-bold hover:text-blue-700 transition-colors">
                  Inloggen
                </Link>
                <button className="bg-[#f97316] hover:bg-orange-600 text-white px-6 py-2.5 rounded-lg font-semibold transition-all shadow-sm shadow-orange-500/30 hover:shadow-orange-500/50">
                  Gratis Uitproberen
                </button>
              </div>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center">
              <button 
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="text-slate-600 hover:text-[#1e3a8a] focus:outline-none"
              >
                {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white border-b border-slate-100 absolute w-full">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 shadow-lg">
              <a href="#features" onClick={(e) => handleNavClick(e, 'features')} className="block px-3 py-2 text-base font-medium text-slate-700 hover:text-[#1e3a8a] hover:bg-slate-50 rounded-md">Functionaliteiten</a>
              <a href="#werkwijze" onClick={(e) => handleNavClick(e, 'werkwijze')} className="block px-3 py-2 text-base font-medium text-slate-700 hover:text-[#1e3a8a] hover:bg-slate-50 rounded-md">Werkwijze</a>
              <a href="#tarieven" onClick={(e) => handleNavClick(e, 'tarieven')} className="block px-3 py-2 text-base font-medium text-slate-700 hover:text-[#1e3a8a] hover:bg-slate-50 rounded-md">Tarieven</a>
              
              <div className="mt-4 pt-4 border-t border-slate-100 flex flex-col gap-3 px-3 pb-2">
                <Link to="/dashboard" className="w-full text-center text-[#1e3a8a] hover:bg-blue-50 py-3 rounded-lg font-bold transition-colors">
                  Inloggen
                </Link>
                <button className="w-full bg-[#f97316] text-white px-6 py-3 rounded-lg font-semibold shadow-sm">
                  Gratis Uitproberen
                </button>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* 🚀 Sectie 1: Hero */}
      <section className="relative bg-white pt-20 pb-28 lg:pt-32 lg:pb-40 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
            
            {/* Tekst Links */}
            <div className="max-w-2xl">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#1e3a8a] tracking-tight leading-[1.15] mb-6">
                Minder administratie.<br/>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#1e3a8a] to-blue-600">
                  Meer focus op de perfecte bespanning.
                </span>
              </h1>
              <p className="text-lg sm:text-xl text-slate-600 mb-8 leading-relaxed">
                De alles-in-één app voor bespanners en teams. Beheer je snaren, plan je werk en laat klanten zelf hun racket inleveren via onze slimme drop-off functie.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button className="flex items-center justify-center gap-2 bg-[#f97316] hover:bg-orange-600 text-white px-8 py-4 rounded-xl font-bold text-lg transition-all shadow-lg shadow-orange-500/30 hover:shadow-orange-500/50 hover:-translate-y-0.5">
                  Start nu gratis
                  <ArrowRight className="w-5 h-5" />
                </button>
                <button className="flex items-center justify-center gap-2 bg-slate-50 hover:bg-slate-100 text-[#1e3a8a] px-8 py-4 rounded-xl font-bold text-lg transition-all border border-slate-200">
                  Hoe werkt het?
                </button>
              </div>
              <div className="mt-8 flex items-center gap-4 text-sm text-slate-500 font-medium">
                <div className="flex items-center gap-1">
                  <CheckCircle2 className="w-4 h-4 text-green-500" /> Geen creditcard nodig
                </div>
                <div className="flex items-center gap-1">
                  <CheckCircle2 className="w-4 h-4 text-green-500" /> Binnen 2 minuten actief
                </div>
              </div>
            </div>

            {/* Afbeelding Rechts */}
            <div className="relative mx-auto w-full max-w-lg lg:max-w-none">
              <div className="absolute -inset-4 bg-gradient-to-tr from-blue-100 to-orange-50 rounded-[2.5rem] blur-2xl opacity-50"></div>
              <img 
                src="/stringing.png" 
                alt="Bespanner aan het werk achter een bespanmachine" 
                className="relative rounded-[2rem] shadow-2xl border border-slate-100/50 object-cover aspect-[4/3] w-full"
                onError={(e) => {
                  e.currentTarget.src = "/stringing.png";
                }}
              />
              
              {/* Floating Badge */}
              <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-2xl shadow-xl border border-slate-100 flex items-center gap-4">
                <div className="bg-green-100 p-3 rounded-full">
                  <CheckCircle2 className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-900">Racket gereed</p>
                  <p className="text-xs text-slate-500">Klant automatisch gemaild</p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ⚙️ Sectie 2: Dashboard & Planning */}
      <section id="features" className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1e3a8a] mb-4">Volledige controle over je werkplaats</h2>
            <p className="text-lg text-slate-600">Alles wat je nodig hebt om je bespanservice soepel en professioneel te laten draaien, verpakt in één overzichtelijk dashboard.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
              <div className="w-14 h-14 bg-blue-50 rounded-xl flex items-center justify-center mb-6">
                <Calendar className="w-7 h-7 text-[#1e3a8a]" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Slim Plannen</h3>
              <p className="text-slate-600 leading-relaxed">
                Weet precies welke rackets wanneer af moeten zijn. Voorkom stress tijdens drukke toernooien of competitieweken met een helder overzicht.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
              <div className="w-14 h-14 bg-orange-50 rounded-xl flex items-center justify-center mb-6">
                <Users className="w-7 h-7 text-[#f97316]" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Team Management</h3>
              <p className="text-slate-600 leading-relaxed">
                Meerdere bespanners in je winkel of op locatie? Wijs rackets eenvoudig toe aan de juiste persoon binnen je team en volg de voortgang.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
              <div className="w-14 h-14 bg-blue-50 rounded-xl flex items-center justify-center mb-6">
                <Activity className="w-7 h-7 text-[#1e3a8a]" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Snaren Voorraad</h3>
              <p className="text-slate-600 leading-relaxed">
                Sla alle type snaren op in het systeem. Weet altijd precies wat je nog op voorraad hebt en ontdek wat de meest populaire keuzes zijn onder je klanten.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 📱 Sectie 3: De Drop-off Functie (USP) */}
      <section className="py-24 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            
            {/* Afbeelding Links */}
            <div className="relative order-2 lg:order-1">
              <div className="absolute inset-0 bg-blue-100 rounded-[3rem] transform -rotate-3 scale-105 z-0"></div>
              <img 
                src="/jonas-leupe-WargGLQW_Yk-unsplash.jpg" 
                alt="Tennisser scant QR code bij inleverpunt" 
                className="relative z-10 rounded-[2.5rem] shadow-xl object-cover aspect-square sm:aspect-[4/3] lg:aspect-[4/5] w-full"
                onError={(e) => {
                  e.currentTarget.src = "/jonas-leupe-WargGLQW_Yk-unsplash.jpg";
                }}
              />
              
              {/* Mockup Overlay */}
              <div className="absolute top-1/2 -right-8 sm:-right-12 transform -translate-y-1/2 z-20 hidden sm:block">
                <div className="bg-white p-4 rounded-3xl shadow-2xl border border-slate-100 w-64">
                   <div className="bg-slate-50 rounded-2xl p-4 text-center">
                      <Smartphone className="w-10 h-10 text-[#f97316] mx-auto mb-2" />
                      <p className="font-bold text-sm text-slate-900">Scan om in te leveren</p>
                      <div className="mt-4 p-2 bg-white rounded-xl shadow-inner flex justify-center">
                        <div className="grid grid-cols-4 gap-1 w-24 h-24">
                           {[...Array(16)].map((_, i) => (
                             <div key={i} className={`bg-slate-800 rounded-sm ${Math.random() > 0.5 ? 'opacity-100' : 'opacity-20'}`}></div>
                           ))}
                        </div>
                      </div>
                   </div>
                </div>
              </div>
            </div>

            {/* Tekst Rechts */}
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-100 text-orange-700 text-sm font-semibold mb-6">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-500"></span>
                </span>
                Onze Unieke Feature
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1e3a8a] mb-6 leading-tight">
                Laat de klant het voorwerk doen.
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Zelf geen eindeloze intake-formulieren meer overtypen. Klanten scannen een QR-code op de inleverlocatie en vullen zelf veilig hun naam, racket en telefoonnummer in op hun eigen telefoon.
              </p>
              
              <ul className="space-y-5">
                {[
                  "Klant voert contact- en racketgegevens in.",
                  "App slaat de aanvraag direct op in jouw dashboard.",
                  "Jij hoeft alleen nog maar de snaarkeuze en het aantal kilo's af te stemmen."
                ].map((item, index) => (
                  <li key={index} className="flex items-start">
                    <div className="flex-shrink-0 mt-1">
                      <CheckCircle2 className="w-6 h-6 text-[#f97316]" />
                    </div>
                    <p className="ml-4 text-slate-700 font-medium text-lg leading-snug">{item}</p>
                  </li>
                ))}
              </ul>

              <div className="mt-10">
                <button className="text-[#1e3a8a] font-bold inline-flex items-center gap-2 hover:text-blue-700 transition-colors group">
                  Bekijk hoe de drop-off werkt 
                  <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 🔄 Sectie 4: Hoe het werkt */}
      <section id="werkwijze" className="py-24 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1e3a8a] mb-4">Hoe het werkt in 3 simpele stappen</h2>
            <p className="text-lg text-slate-600">Van inleveren tot ophalen, bespanapp.nl stroomlijnt het hele proces voor jou én je klant.</p>
          </div>

          <div className="relative">
            <div className="hidden md:block absolute top-12 left-[10%] right-[10%] h-0.5 bg-slate-200 z-0"></div>
            <div className="grid md:grid-cols-3 gap-12 relative z-10">
              
              <div className="flex flex-col items-center text-center">
                <div className="w-24 h-24 bg-white rounded-full shadow-md border border-slate-100 flex items-center justify-center mb-6 relative">
                  <div className="absolute inset-0 bg-blue-50 rounded-full scale-0 hover:scale-100 transition-transform origin-center -z-10"></div>
                  <span className="text-3xl font-extrabold text-[#f97316]">1</span>
                </div>
                <h3 className="text-xl font-bold text-[#1e3a8a] mb-3">Inleveren</h3>
                <p className="text-slate-600">De speler meldt het racket aan via de handige drop-off link of QR-code.</p>
              </div>

              <div className="flex flex-col items-center text-center">
                <div className="w-24 h-24 bg-white rounded-full shadow-md border border-slate-100 flex items-center justify-center mb-6 relative">
                  <span className="text-3xl font-extrabold text-[#f97316]">2</span>
                </div>
                <h3 className="text-xl font-bold text-[#1e3a8a] mb-3">Afstemmen & Bespannen</h3>
                <p className="text-slate-600">Jij hoeft alleen nog te controleren of de juiste informatie is ingevoerd.</p>
              </div>

              <div className="flex flex-col items-center text-center">
                <div className="w-24 h-24 bg-white rounded-full shadow-md border border-slate-100 flex items-center justify-center mb-6 relative">
                  <span className="text-3xl font-extrabold text-[#f97316]">3</span>
                </div>
                <h3 className="text-xl font-bold text-[#1e3a8a] mb-3">Ophalen</h3>
                <p className="text-slate-600">Stuur eenvoudig een berichtje naar de speler zodra het racket klaar is inclusief betaalverzoek.</p>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* 💶 Sectie 4.5: Tarieven */}
      <section id="tarieven" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1e3a8a] mb-4">Heldere tarieven, geen verrassingen</h2>
            <p className="text-lg text-slate-600">Kies het pakket dat bij jouw bespanservice past. Probeer alle functionaliteiten 14 dagen gratis.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto items-center">
            <div className="bg-slate-50 rounded-3xl p-8 border border-slate-200">
              <h3 className="text-2xl font-bold text-[#1e3a8a] mb-2">Solo Bespanner</h3>
              <p className="text-slate-500 mb-6">Perfect voor de onafhankelijke bespanner vanuit huis.</p>
              <div className="mb-8 flex items-baseline gap-1">
                <span className="text-4xl font-extrabold text-slate-900">€14</span>
                <span className="text-slate-500 font-medium"> /maand</span>
              </div>
              <ul className="space-y-4 mb-8">
                <li className="flex items-center gap-3"><CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" /> <span className="text-slate-700">Onbeperkt rackets bespannen</span></li>
                <li className="flex items-center gap-3"><CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" /> <span className="text-slate-700">Slimme drop-off QR-codes</span></li>
                <li className="flex items-center gap-3"><CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" /> <span className="text-slate-700">Basis snarenvoorraad beheer</span></li>
                <li className="flex items-center gap-3"><CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" /> <span className="text-slate-700">Klanten hebben eigen account om alle bespanningen in te zien</span></li>
              </ul>
              <button className="w-full bg-white text-[#1e3a8a] border-2 border-[#1e3a8a] hover:bg-slate-100 px-6 py-3 rounded-xl font-bold transition-colors">
                Start 14 dagen gratis
              </button>
            </div>

            <div className="bg-[#1e3a8a] rounded-3xl p-8 border-2 border-orange-500 relative shadow-2xl transform md:-translate-y-4">
              <div className="absolute top-0 right-8 transform -translate-y-1/2">
                <span className="bg-[#f97316] text-white px-4 py-1.5 rounded-full text-sm font-bold shadow-lg">Meest Gekozen</span>
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Winkel & Club</h3>
              <p className="text-blue-200 mb-6">Voor teams, tennisscholen en professionele winkels.</p>
              <div className="mb-8 flex items-baseline gap-1">
                <span className="text-4xl font-extrabold text-white">€29</span>
                <span className="text-blue-200 font-medium"> /maand</span>
              </div>
              <ul className="space-y-4 mb-8">
                <li className="flex items-center gap-3"><CheckCircle2 className="w-5 h-5 text-[#f97316] flex-shrink-0" /> <span className="text-white">Alles uit het Solo pakket</span></li>
                <li className="flex items-center gap-3"><CheckCircle2 className="w-5 h-5 text-[#f97316] flex-shrink-0" /> <span className="text-white">Meerdere medewerkers (Team)</span></li>
                <li className="flex items-center gap-3"><CheckCircle2 className="w-5 h-5 text-[#f97316] flex-shrink-0" /> <span className="text-white">Uitgebreide statistieken & omzet</span></li>
                <li className="flex items-center gap-3"><CheckCircle2 className="w-5 h-5 text-[#f97316] flex-shrink-0" /> <span className="text-white">Mollie integratie, ontvang direct betalingen via bespanapp.nl</span></li>
              </ul>
              <button className="w-full bg-[#f97316] hover:bg-orange-600 text-white px-6 py-3 rounded-xl font-bold shadow-lg shadow-orange-500/30 transition-all hover:-translate-y-0.5">
                Start 14 dagen gratis
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 🏁 Footer */}
      <section className="bg-blue-50 py-20 lg:py-28 relative overflow-hidden">
        <div className="absolute top-0 right-0 -mr-20 -mt-20 w-64 h-64 rounded-full bg-blue-100 opacity-50 blur-3xl"></div>
        <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-80 h-80 rounded-full bg-orange-100 opacity-50 blur-3xl"></div>
        
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <h2 className="text-3xl md:text-5xl font-extrabold text-[#1e3a8a] mb-6">
            Klaar om je bespanservice te professionaliseren?
          </h2>
          <p className="text-xl text-slate-600 mb-10 max-w-2xl mx-auto">
            Sluit je aan bij de groeiende groep bespanners die minder tijd kwijt is aan administratie en meer tijd heeft voor vakwerk.
          </p>
          <button className="bg-[#f97316] hover:bg-orange-600 text-white px-10 py-5 rounded-xl font-bold text-lg lg:text-xl transition-all shadow-xl shadow-orange-500/30 hover:shadow-orange-500/50 hover:-translate-y-1">
            Maak vandaag nog je account aan
          </button>
          <p className="mt-6 text-sm text-slate-500">14 dagen gratis proberen. Geen verplichtingen.</p>
        </div>
      </section>

      <footer className="bg-white border-t border-slate-200 pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            <div className="col-span-1 md:col-span-2">
              <img src="/logo.png" alt="Bespanapp.nl Logo" className="h-10 w-auto object-contain mb-6" />
              <p className="text-slate-500 max-w-xs">
                De slimme software oplossing voor tennisbespanners, winkels en clubs.
              </p>
            </div>
            <div>
              <h4 className="font-bold text-slate-900 mb-4">Product</h4>
              <ul className="space-y-3">
                <li><a href="#features" onClick={(e) => handleNavClick(e, 'features')} className="text-slate-500 hover:text-[#f97316] transition-colors">Functionaliteiten</a></li>
                <li><a href="#tarieven" onClick={(e) => handleNavClick(e, 'tarieven')} className="text-slate-500 hover:text-[#f97316] transition-colors">Prijzen</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-slate-900 mb-4">Ondersteuning</h4>
              <ul className="space-y-3">
                <li><a href="/support/voorwaarden" className="text-slate-500 hover:text-[#f97316] transition-colors">Algemene Voorwaarden</a></li>
                <li><a href="/support/privacy" target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-[#f97316] transition-colors">Privacy Verklaring</a></li>
                <li><a href="/support/contact" className="text-slate-500 hover:text-[#f97316] transition-colors">Contact</a></li>

              </ul>
            </div>
          </div>
          <div className="border-t border-slate-100 pt-8 flex justify-between items-center">
            <p className="text-slate-400 text-sm">© {new Date().getFullYear()} bespanapp.nl. Alle rechten voorbehouden.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}